import React, { useState } from "react";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Drawer, Form, Input, message, Popconfirm, Switch, Table, Tabs, Typography } from "antd";
import ReactMarkdown from "react-markdown";

import { supabase } from "~/store/initializeStore";

// Owner-only settings for the support intake agent (TRE-1548): prompt "skills"
// (like the sales agent) and a company/program knowledge base. Both live in
// support_agent_resource, distinguished by `kind`; RLS limits access to the
// account owner, so this component assumes the caller gated visibility already.

type ResourceKind = "skill" | "knowledge";

interface SupportAgentResourceRow {
  id: string;
  kind: string;
  name: string;
  description: string;
  content: string;
  enabled: boolean;
  sortOrder: number;
  updatedAt: string | null;
  createdAt: string;
}

interface ResourceFormValues {
  name: string;
  description?: string;
  content: string;
  enabled: boolean;
  sortOrder?: number;
}

const RESOURCES_QUERY_KEY = "support_agent_resources";

const KIND_COPY: Record<ResourceKind, { title: string; addLabel: string; empty: string; contentLabel: string }> = {
  skill: {
    title: "Skille agenta — wytyczne zachowania dopisywane do promptu intake agenta.",
    addLabel: "Dodaj skill",
    empty: "Brak skilli — agent działa na bazowym prompcie.",
    contentLabel: "Treść skilla (markdown)",
  },
  knowledge: {
    title: "Baza wiedzy o firmie, programach i produktach — agent korzysta z niej w rozmowach o zgłoszeniach.",
    addLabel: "Dodaj wpis",
    empty: "Brak wpisów — dodaj wiedzę o firmie, programach, cennikach itd.",
    contentLabel: "Treść wpisu (markdown)",
  },
};

const ResourceList: React.FC<{ kind: ResourceKind }> = ({ kind }) => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm<ResourceFormValues>();
  const [editorOpen, setEditorOpen] = useState(false);
  const [activeResource, setActiveResource] = useState<SupportAgentResourceRow | null>(null);
  const contentValue = Form.useWatch("content", form);
  const copy = KIND_COPY[kind];
  const queryKey = [RESOURCES_QUERY_KEY, kind];

  const resourcesQuery = useQuery({
    queryKey,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("support_agent_resource")
        .select("*")
        .eq("kind", kind)
        .order("sortOrder", { ascending: true })
        .order("name", { ascending: true });

      if (error) throw new Error(error.message);
      return (data ?? []) as SupportAgentResourceRow[];
    },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [RESOURCES_QUERY_KEY] });

  const saveMutation = useMutation({
    mutationFn: async (values: ResourceFormValues) => {
      const payload = {
        kind,
        name: values.name,
        description: values.description ?? "",
        content: values.content ?? "",
        enabled: values.enabled,
        sortOrder: Number(values.sortOrder ?? 0),
        updatedAt: new Date().toISOString(),
      };

      const { error } = activeResource
        ? await supabase.from("support_agent_resource").update(payload).eq("id", activeResource.id)
        : await supabase.from("support_agent_resource").insert(payload);

      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      setEditorOpen(false);
      setActiveResource(null);
      form.resetFields();
      await invalidate();
      void message.success("Zapisano");
    },
    onError: (error) => void message.error(error instanceof Error ? error.message : "Błąd zapisu"),
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const { error } = await supabase
        .from("support_agent_resource")
        .update({ enabled, updatedAt: new Date().toISOString() })
        .eq("id", id);
      if (error) throw new Error(error.message);
    },
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("support_agent_resource").delete().eq("id", id);
      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      await invalidate();
      void message.success("Usunięto");
    },
  });

  const openEditor = (resource: SupportAgentResourceRow | null) => {
    setActiveResource(resource);
    form.setFieldsValue(
      resource
        ? {
            name: resource.name,
            description: resource.description,
            content: resource.content,
            enabled: resource.enabled,
            sortOrder: resource.sortOrder,
          }
        : { name: "", description: "", content: "", enabled: true, sortOrder: 0 },
    );
    setEditorOpen(true);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-4">
        <Typography.Text type="secondary">{copy.title}</Typography.Text>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openEditor(null)}>
          {copy.addLabel}
        </Button>
      </div>

      <Table<SupportAgentResourceRow>
        rowKey="id"
        size="small"
        loading={resourcesQuery.isLoading}
        dataSource={resourcesQuery.data ?? []}
        locale={{ emptyText: copy.empty }}
        pagination={false}
        columns={[
          { title: "Nazwa", dataIndex: "name" },
          { title: "Opis", dataIndex: "description", ellipsis: true },
          {
            title: "Aktywny",
            dataIndex: "enabled",
            width: 90,
            render: (enabled: boolean, record) => (
              <Switch
                size="small"
                checked={enabled}
                onChange={(next) => toggleMutation.mutate({ id: record.id, enabled: next })}
              />
            ),
          },
          {
            title: "",
            key: "actions",
            width: 90,
            render: (_, record) => (
              <div className="flex gap-1">
                <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEditor(record)} />
                <Popconfirm
                  title="Usunąć wpis?"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => deleteMutation.mutate(record.id)}
                >
                  <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </div>
            ),
          },
        ]}
      />

      <Drawer
        title={activeResource ? "Edytuj" : copy.addLabel}
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setActiveResource(null);
        }}
        width={760}
        extra={
          <Button type="primary" loading={saveMutation.isLoading} onClick={() => form.submit()}>
            Zapisz
          </Button>
        }
      >
        <Form form={form} layout="vertical" onFinish={(values) => saveMutation.mutate(values)}>
          <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
            <Form.Item name="name" label="Nazwa" rules={[{ required: true, message: "Podaj nazwę" }]}>
              <Input />
            </Form.Item>
            <div className="flex gap-4">
              {/* Ordering matters for skills (prompt sections apply in order); knowledge
                  entries are independent facts, so the field would just be noise. */}
              {kind === "skill" && (
                <Form.Item name="sortOrder" label="Kolejność" className="flex-1">
                  <Input type="number" />
                </Form.Item>
              )}
              <Form.Item name="enabled" label="Aktywny" valuePropName="checked">
                <Switch />
              </Form.Item>
            </div>
          </div>
          <Form.Item name="description" label="Opis">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Tabs
            items={[
              {
                key: "editor",
                label: "Markdown",
                children: (
                  <Form.Item
                    name="content"
                    label={copy.contentLabel}
                    rules={[{ required: true, message: "Podaj treść" }]}
                  >
                    <Input.TextArea rows={16} className="font-mono" />
                  </Form.Item>
                ),
              },
              {
                key: "preview",
                label: "Podgląd",
                children: (
                  <div className="prose max-w-none rounded-lg border border-gray-200 bg-white p-4">
                    <ReactMarkdown>{contentValue || ""}</ReactMarkdown>
                  </div>
                ),
              },
            ]}
          />
        </Form>
      </Drawer>
    </div>
  );
};

export const SupportSettings: React.FC = () => (
  <Tabs
    items={[
      { key: "skills", label: "Skille agenta", children: <ResourceList kind="skill" /> },
      { key: "knowledge", label: "Baza wiedzy", children: <ResourceList kind="knowledge" /> },
    ]}
  />
);
